import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { Modal } from "../../components/ui/Modal";
import type { Role, UnlinkedMember, UserListItem } from "../../types";

interface Props {
  user: UserListItem | null; // null = neuer Benutzer
  roles: Role[];
  onClose: () => void;
  onSaved: () => void;
}

const NEW_MEMBER = "new";

/** Sucht unter den noch unverknüpften Mitgliedern einen Namenstreffer, um die
 *  Auswahl sinnvoll vorzubelegen - der Admin kann das jederzeit überschreiben,
 *  siehe Kommentar in utils/memberSync.ts (Backend) zum Hintergrund. */
function findExactMatch(members: UnlinkedMember[], fullName: string): UnlinkedMember | undefined {
  const normalized = fullName.trim().toLowerCase();
  return members.find((m) => `${m.first_name} ${m.last_name}`.trim().toLowerCase() === normalized);
}

export function UserForm({ user, roles, onClose, onSaved }: Props) {
  const [username, setUsername] = useState(user?.username ?? "");
  const [fullName, setFullName] = useState(user?.full_name ?? "");
  const [roleId, setRoleId] = useState(user?.role_id ?? roles[0]?.id ?? "");
  const [password, setPassword] = useState("");
  const [unlinkedMembers, setUnlinkedMembers] = useState<UnlinkedMember[]>([]);
  const [linkMemberId, setLinkMemberId] = useState<string>(NEW_MEMBER);
  const [linkMemberTouched, setLinkMemberTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Mitglied-Auswahl betrifft nur das Neuanlegen - beim Bearbeiten eines
  // bestehenden Benutzers ist die Verknüpfung bereits vorhanden/unveränderlich.
  useEffect(() => {
    if (user) return;
    api.get<UnlinkedMember[]>("/api/users/unlinked-members").then(setUnlinkedMembers);
  }, [user]);

  // Schlägt beim Tippen des Namens live einen Namenstreffer vor - solange
  // der Admin die Auswahl nicht selbst manuell geändert hat (sonst würde
  // jede weitere Eingabe eine bewusste manuelle Wahl wieder überschreiben).
  useEffect(() => {
    if (user || linkMemberTouched) return;
    const match = findExactMatch(unlinkedMembers, fullName);
    setLinkMemberId(match ? String(match.id) : NEW_MEMBER);
  }, [fullName, unlinkedMembers, user, linkMemberTouched]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (user) {
        await api.put(`/api/users/${user.id}`, { fullName, roleId: Number(roleId) });
      } else {
        await api.post("/api/users", {
          username, fullName, roleId: Number(roleId), password,
          linkMemberId: linkMemberId === NEW_MEMBER ? null : Number(linkMemberId),
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={user ? "Benutzer bearbeiten" : "Neuer Benutzer"}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn-primary" form="user-form" disabled={submitting}>Speichern</button>
        </>
      }
    >
      <form id="user-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}

        <div>
          <label className="label">Benutzername *</label>
          <input className="input" value={username} onChange={(e) => setUsername(e.target.value)} required disabled={!!user} />
        </div>
        <div>
          <label className="label">Vollständiger Name *</label>
          <input className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
        </div>
        <div>
          <label className="label">Rolle *</label>
          <select className="input" value={roleId} onChange={(e) => setRoleId(e.target.value as any)} required>
            {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        {!user && (
          <div>
            <label className="label">Initiales Passwort *</label>
            <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required />
          </div>
        )}
        {!user && (
          <div>
            <label className="label">Mitglied verknüpfen</label>
            <select
              className="input"
              value={linkMemberId}
              onChange={(e) => { setLinkMemberId(e.target.value); setLinkMemberTouched(true); }}
            >
              <option value={NEW_MEMBER}>— Neues Mitglied anlegen —</option>
              {unlinkedMembers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.first_name} {m.last_name}{m.rank ? ` (${m.rank})` : ""}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Ist diese Person bereits als Mitglied erfasst, hier auswählen - verhindert doppelte Einträge.
            </p>
          </div>
        )}
      </form>
    </Modal>
  );
}
